import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getLeadForm,
  getLeadPresets,
  saveLeadForm,
  type LeadFieldSpec,
  type LeadFieldType,
} from "../api/leads";

// Per-campaign lead-capture form editor (F6). The questions a rep asks are the
// operator's business, not the product's: an operator starts from a preset
// (or the generic default) and edits from there. The name is always on the
// form and is not editable here.

const TYPES: { value: LeadFieldType; label: string }[] = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "select", label: "Choice" },
  { value: "boolean", label: "Yes / No" },
  { value: "date", label: "Date" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone number" },
];

function slug(label: string): string {
  const s = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
  return /^[a-z]/.test(s) ? s : s ? `f_${s}` : "";
}

interface Row extends LeadFieldSpec {
  keyTouched: boolean;
  optionsText: string;
}

function toRow(f: LeadFieldSpec): Row {
  return { ...f, keyTouched: true, optionsText: f.options.join(", ") };
}

function fromRow(r: Row): LeadFieldSpec {
  return {
    key: r.key,
    label: r.label,
    type: r.type,
    options: r.type === "select" ? r.optionsText.split(",").map((s) => s.trim()).filter(Boolean) : [],
    required: r.required,
    pii: r.pii,
    placeholder: r.placeholder || null,
  };
}

function errorText(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string } }; message?: string };
  return ax?.response?.data?.detail || ax?.message || "please try again";
}

export function LeadFormEditor({
  campaign,
  onClose,
}: {
  campaign: { campaign_id: number; name: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const form = useQuery({
    queryKey: ["lead-form", campaign.campaign_id],
    queryFn: () => getLeadForm(campaign.campaign_id),
  });
  const presets = useQuery({ queryKey: ["lead-presets"], queryFn: getLeadPresets });

  const [rows, setRows] = useState<Row[] | null>(null);
  useEffect(() => {
    if (rows === null && form.data) setRows(form.data.fields.map(toRow));
  }, [form.data, rows]);

  const save = useMutation({
    mutationFn: (body: { fields: LeadFieldSpec[] } | { reset: true }) =>
      saveLeadForm(campaign.campaign_id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lead-form", campaign.campaign_id] });
      onClose();
    },
  });

  function update(i: number, patch: Partial<Row>) {
    setRows((rs) => (rs ?? []).map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }
  function move(i: number, dir: -1 | 1) {
    setRows((rs) => {
      const list = [...(rs ?? [])];
      const j = i + dir;
      if (j < 0 || j >= list.length) return list;
      [list[i], list[j]] = [list[j], list[i]];
      return list;
    });
  }

  const list = rows ?? [];
  const inputClass = "w-full border border-gray-300 rounded px-2 py-1 text-sm";

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-full overflow-auto space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">Lead form — {campaign.name}</h2>
            <p className="text-xs text-gray-500">
              What agents fill in while on a call. First and last name are always asked. Saves as they type; nothing here
              blocks a transfer or a wrap-up.
            </p>
          </div>
          {form.data && !form.data.customised && (
            <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-600 whitespace-nowrap">default form</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label htmlFor="lead-preset" className="text-gray-600">Start from</label>
          <select
            id="lead-preset"
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value=""
            onChange={(e) => {
              const p = (presets.data ?? []).find((x) => x.id === e.target.value);
              if (p) setRows(p.fields.map(toRow));
            }}
          >
            <option value="">— choose a preset —</option>
            {(presets.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
            onClick={() =>
              setRows((rs) => [
                ...(rs ?? []),
                { key: "", label: "", type: "text", options: [], required: false, pii: false, placeholder: null, keyTouched: false, optionsText: "" },
              ])
            }
          >
            + Add field
          </button>
        </div>

        {form.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {form.isError && <p className="text-sm text-red-600">{errorText(form.error)}</p>}

        {rows !== null && (
          <div className="space-y-2">
            {list.length === 0 && (
              <p className="text-sm text-gray-500">Name only. Add a field or pick a preset.</p>
            )}
            {list.map((r, i) => (
              <div key={i} className="rounded border border-gray-200 p-3 grid grid-cols-12 gap-2 items-end" data-testid="lead-field-row">
                <div className="col-span-5">
                  <label htmlFor={`lf-label-${i}`} className="block text-xs text-gray-500 mb-1">Question</label>
                  <input
                    id={`lf-label-${i}`}
                    className={inputClass}
                    value={r.label}
                    onChange={(e) =>
                      update(i, { label: e.target.value, key: r.keyTouched ? r.key : slug(e.target.value) })
                    }
                  />
                </div>
                <div className="col-span-3">
                  <label htmlFor={`lf-key-${i}`} className="block text-xs text-gray-500 mb-1">Key</label>
                  <input
                    id={`lf-key-${i}`}
                    className={`${inputClass} font-mono`}
                    value={r.key}
                    onChange={(e) => update(i, { key: e.target.value, keyTouched: true })}
                  />
                </div>
                <div className="col-span-3">
                  <label htmlFor={`lf-type-${i}`} className="block text-xs text-gray-500 mb-1">Type</label>
                  <select
                    id={`lf-type-${i}`}
                    className={inputClass}
                    value={r.type}
                    onChange={(e) => update(i, { type: e.target.value as LeadFieldType })}
                  >
                    {TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-1 flex justify-end gap-1">
                  <button type="button" onClick={() => move(i, -1)} className="text-gray-400 hover:text-gray-700" aria-label="Move up">↑</button>
                  <button type="button" onClick={() => move(i, 1)} className="text-gray-400 hover:text-gray-700" aria-label="Move down">↓</button>
                  <button
                    type="button"
                    onClick={() => setRows((rs) => (rs ?? []).filter((_, j) => j !== i))}
                    className="text-red-500 hover:text-red-700"
                    aria-label="Remove field"
                  >
                    ×
                  </button>
                </div>
                {r.type === "select" && (
                  <div className="col-span-12">
                    <label htmlFor={`lf-options-${i}`} className="block text-xs text-gray-500 mb-1">Choices (comma-separated)</label>
                    <input
                      id={`lf-options-${i}`}
                      className={inputClass}
                      value={r.optionsText}
                      placeholder="Yes, No, Not sure"
                      onChange={(e) => update(i, { optionsText: e.target.value })}
                    />
                  </div>
                )}
                <div className="col-span-12 flex flex-wrap gap-4 text-xs text-gray-600">
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={r.required} onChange={(e) => update(i, { required: e.target.checked })} />
                    Prompt the agent to ask (never blocks saving)
                  </label>
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={r.pii} onChange={(e) => update(i, { pii: e.target.checked })} />
                    Private — hide from supervisors on masked accounts
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-gray-500">
          On accounts with number masking, email and phone fields are masked on the Captured Leads page automatically;
          free text is shown as typed unless marked private.
        </p>

        {save.isError && <p className="text-sm text-red-600" role="alert">{errorText(save.error)}</p>}

        <div className="flex items-center justify-between gap-2 pt-2">
          <button
            type="button"
            className="text-sm text-gray-500 hover:underline"
            onClick={() => {
              if (confirm("Reset this campaign's lead form to the default?")) save.mutate({ reset: true });
            }}
          >
            Reset to default
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded text-sm bg-gray-100 hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="button"
              disabled={rows === null || save.isPending}
              onClick={() => save.mutate({ fields: list.map(fromRow) })}
              className="px-4 py-2 rounded text-sm bg-brand hover:bg-brand-dark text-white disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save form"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
